import React, { useState } from 'react';
import { OverlayTrigger, Popover, useClassNameMapper } from 'react-bootstrap';
import { SharedComponent } from '../../../enums';
import Icons from '../../../icons';
import Button from '../../common/Button/Button';
import { ButtonVariant, LoadingButtonVariant } from '../../common/Button/enums';
import ConfirmationDialog from '../../common/ConfirmationDialog/ConfirmationDialog';
import { IconSize } from '../../common/Icon/enums';
import Icon from '../../common/Icon/Icon';
import useTranslation from '../../useTranslation';
import type TagOption from './TagOption';
import localStyle from './TagRemoveButton.module.pcss';

interface Props {
  /**
   * The tag to remove on button click.
   */
  tag: TagOption;

  /**
   * @callback
   * A callback function called when a tag is removed.
   */
  onRemove: (tag: TagOption) => Promise<void>;

  /**
   * To disable the button
   */
  isDisabled: boolean;
  /**
   * whether field is present in admin app or enduser app
   */
  isAdminSetting?: boolean;
  /**
   * Whether or not to show remove tag dialog
   */
  showRemoveTagDialog?: boolean;
}

/**
 * A button to remove a tag from a message.
 *
 * @constructor
 *
 * @author Dolan Halbrook
 */
const TagRemoveButton: React.FC<React.PropsWithChildren<Props>> = ({
  onRemove,
  tag,
  isDisabled,
  isAdminSetting,
  showRemoveTagDialog
}) => {
  const cx = useClassNameMapper(localStyle);
  const {
    formatMessage,
    FormattedMessage,
    loading: textLoading
  } = useTranslation(SharedComponent.TAG_REMOVE_BUTTON);
  const [showRemoveTagDialogBox, setShowRemoveTagDialogBox] = useState<boolean>(false);

  if (textLoading) {
    return null;
  }

  /**
   * Function to render the remove tag button
   *
   */
  function renderButton(): React.ReactElement {
    return (
      <Button
        title={formatMessage('remove')}
        variant={ButtonVariant.UNSTYLED}
        className={cx('lia-remove')}
        onClick={(): void => {
          if (showRemoveTagDialog) {
            setShowRemoveTagDialogBox(true);
          } else {
            onRemove(tag);
          }
        }}
        data-testid="TagRemoveButton"
        disabled={isDisabled}
        aria-label={formatMessage('removeTag.ariaLabel', { tagName: tag.label })}
      >
        <Icon
          size={IconSize.FROM_CSS}
          icon={Icons.CloseIcon}
          className={cx('lia-remove-icon')}
          testId="TagRemoveButton.close"
        />
      </Button>
    );
  }

  /**
   * Function to render the popover
   *
   */
  function renderPopover(): React.ReactElement {
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Popover id="tag-button">
            <Popover.Content>
              {isAdminSetting ? formatMessage('adminPopoverText') : formatMessage('popoverText')}
            </Popover.Content>
          </Popover>
        }
      >
        {renderButton()}
      </OverlayTrigger>
    );
  }

  /**
   * Renders the confirmation dialog before removing the tag.
   */
  function renderRemoveTagDialog(): React.ReactElement {
    return (
      <ConfirmationDialog
        show={showRemoveTagDialog}
        onHide={(): void => setShowRemoveTagDialogBox(false)}
        onSubmit={async (): Promise<void> => onRemove(tag)}
        onCancel={(): void => setShowRemoveTagDialogBox(false)}
        titleText={formatMessage('removeTagDialogTitle')}
        bodyText={
          <FormattedMessage
            id="removeTagDialogBody"
            values={{
              strong: (...chunks) =>
                (<strong className={cx('text-break')}>{chunks}</strong>) as React.ReactNode,
              br: <br />,
              tagName: tag.label.toUpperCase()
            }}
          />
        }
        submitButtonVariant={ButtonVariant.DANGER}
        submitButtonType={LoadingButtonVariant.LOADING_BUTTON}
        submitButtonText={formatMessage('removeTagDialogButton')}
      />
    );
  }

  return (
    <>
      {isDisabled ? renderPopover() : renderButton()}
      {showRemoveTagDialogBox && renderRemoveTagDialog()}
    </>
  );
};

export default TagRemoveButton;
